import React, { useEffect, useState } from 'react';
import { Button, CircularProgress, Grid, Paper, Tab, Tabs, Toolbar } from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import { DataTable, FormComponent, Notifications, TabPanel } from '..';

import { ComparisonModal } from './ComparisonModal';

function UploadCSVAndReviewComponent({
    additionalFormFields = [],
    columns,
    confirm,
    confirmationModalKeyName = '',
    downloadTemplate,
    downloadTemplateText = 'Download Template',
    downloadTemplateWithDummyData,
    downloadTemplateWithDummyDataText = 'Download Template with Dummy Data',
    expandable = false,
    expansionComponent = null,
    exportUploadResults = false,
    fileLabel,
    hideValidRowsOnError = false,
    maxNumFiles = null,
    multipleFiles = false,
    onSuccess,
    showConfirmationModal = false,
    subtitle,
    title,
    upload,
}) {
    const [notification, setNotification] = useState({ text: null, level: null });
    const [uploadResults, setUploadResults] = useState(null);
    const [toolbarActions, setToolbarActions] = useState([]);
    const [formFields, setFormFields] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [isConfirming, setIsConfirming] = useState(false);

    const clearAction = {
        name: `Clear File${multipleFiles ? 's' : ''}`,
        action: () => {
            setToolbarActions([]);
            setUploadResults(null);
        },
    };
    const disabledUploadAction = {
        name: 'Confirm Upload',
        action: () => {},
        disabled: true,
    };
    let uploadAction = null;

    useEffect(() => {
        const ff = additionalFormFields;
        ff.push({
            name: 'file',
            inputElement: 'file',
            accept: ['.csv'],
            multiple: multipleFiles,
            maxNumFiles,
            label: fileLabel,
            gridValue: 12,
        });
        setFormFields(ff);
    }, []);

    const confirmUpload = async (key) => {
        setIsConfirming(true);
        setToolbarActions([clearAction, disabledUploadAction]);

        if (!key) {
            setNotification({ text: 'No key provided for upload', level: 'error' });
            setIsConfirming(false);
            return;
        }

        if (confirm instanceof Function === false) {
            console.log('No confirm function provided');
            setIsConfirming(false);
            return;
        }

        const res = await confirm(key);

        if (!res.status) {
            setNotification({
                text: `Failed to create uploaded items${res.msg ? `. ${res.msg}` : ''}`,
                level: 'error',
            });

            setToolbarActions([clearAction, uploadAction]);
            setIsConfirming(false);

            return;
        }

        setNotification({ text: 'Items uploaded', level: 'success' });
        setUploadResults(null);
        setToolbarActions([]);
        setIsConfirming(false);
        if (onSuccess instanceof Function) {
            onSuccess(res.data);
        }
    };

    const uploadCSV = async (formData) => {
        if (upload instanceof Function === false) {
            console.log('No upload function provided');
            return;
        }

        setIsUploading(true);

        const res = await upload(formData);

        setIsUploading(false);

        if (!res.status) {
            setNotification({ text: `Failed to get results from upload. ${res.msg}`, level: 'error' });
            return;
        }

        const { data } = res;
        const results = res.data.results
            .filter((row) => !hideValidRowsOnError || !data.error || row?.errors?.length > 0)
            .map((row) => {
                row.errorsArr = row.errors;
                row.errors = row?.errors?.join('. ');
                row.warnings = row?.warnings?.join('. ');
                return row;
            });
        data.results = results;

        const actions = [clearAction];

        if (!data.error) {
            uploadAction = {
                name: 'Confirm Upload',
                action: () => {
                    const { key } = data;
                    confirmUpload(key);
                },
            };
            actions.push(uploadAction);
        }

        setUploadResults(data);
        setToolbarActions(actions);
    };

    return (
        <div>
            <Notifications options={notification} />
            {!!title && <h3>{title}</h3>}
            {!!subtitle && <h4>{subtitle}</h4>}
            {isUploading && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '20px 0' }}>
                    <CircularProgress size={24} />
                    <span>Uploading and processing file...</span>
                </div>
            )}
            {!uploadResults && !isUploading && (
                <>
                    <Grid container justifycontent="space-evenly">
                        {downloadTemplateWithDummyData instanceof Function && (
                            <Grid item xs>
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        downloadTemplateWithDummyData();
                                    }}
                                >
                                    {downloadTemplateWithDummyDataText}
                                </Button>
                            </Grid>
                        )}
                        {downloadTemplate instanceof Function && (
                            <Grid item xs>
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        downloadTemplate();
                                    }}
                                >
                                    {downloadTemplateText}
                                </Button>
                            </Grid>
                        )}
                    </Grid>
                    {formFields.length > 0 && (
                        <FormComponent
                            formFields={formFields}
                            onSubmit={async (formData, resetForm) => {
                                await uploadCSV(formData);
                            }}
                        />
                    )}
                </>
            )}
            {showConfirmationModal && uploadResults?.changedData && uploadResults.changedData.some((item) => item) && (
                <ComparisonModal data={uploadResults.changedData} confirmationModalKeyName={confirmationModalKeyName} />
            )}
            {!!uploadResults && (
                <>
                    {uploadResults.error ? (
                        <Alert severity="error">
                            There are errors in the uploaded file. Please review the following errors and upload a
                            corrected csv.
                        </Alert>
                    ) : uploadResults.warning ? (
                        <Alert severity="warning">Some warnings were found. Expand rows to see more.</Alert>
                    ) : (
                        <Alert severity="success">No errors detected. Please review and confirm.</Alert>
                    )}
                    {uploadResults?.errors?.length > 0 && (
                        <div>
                            Errors In File{multipleFiles ? 's' : ''}: {uploadResults.errors.join('. ')}
                        </div>
                    )}
                    {uploadResults?.warnings?.length > 0 && !uploadResults.error && (
                        <div style={{ maxHeight: '400px', overflowY: 'scroll' }}>
                            {uploadResults.warnings.map((item, ind) => (
                                <Alert key={`${ind}`} severity="warning">
                                    {item}
                                </Alert>
                            ))}
                        </div>
                    )}
                    {multipleFiles ? (
                        <>
                            {isConfirming && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '20px 0' }}>
                                    <CircularProgress size={24} />
                                    <span>Confirming upload...</span>
                                </div>
                            )}
                            <ResultsMultipleFiles
                                data={uploadResults.results}
                                columns={columns}
                                toolbarActions={toolbarActions}
                                exportUploadResults={exportUploadResults}
                                isConfirming={isConfirming}
                            />
                        </>
                    ) : (
                        <>
                            {isConfirming && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '20px 0' }}>
                                    <CircularProgress size={24} />
                                    <span>Confirming upload...</span>
                                </div>
                            )}
                            <DataTable
                                columns={columns}
                                toolbarActions={toolbarActions}
                                data={uploadResults.results}
                                rowStyle={(row) => {
                                    if (row.errors?.length) {
                                        return { color: 'red', border: '3px solid red' };
                                    }
                                    if (row.warnings?.length) {
                                        return { color: '#FFB300', border: '3px solid #FFB300' };
                                    }
                                    return {};
                                }}
                                csvExport={exportUploadResults}
                                expandable={expandable}
                                ExpansionComponent={expansionComponent}
                                data-testid="datatable"
                            />
                        </>
                    )}
                </>
            )}
        </div>
    );
}

function ResultsMultipleFiles({ columns, data, exportUploadResults, isConfirming, toolbarActions }) {
    const [selectedTab, setSelectedTab] = useState(0);
    const handleChange = (event, value) => {
        setSelectedTab(value);
    };

    return (
        <Paper>
            <Toolbar>
                {toolbarActions.map((item, index) => (
                    <ToolbarButton key={index} item={item} isConfirming={isConfirming} />
                ))}
            </Toolbar>
            <Tabs
                variant="scrollable"
                onChange={handleChange}
                value={selectedTab}
                indicatorColor="primary"
                textColor="primary"
                centered
            >
                {data.map((item, index) => {
                    let error = false;
                    if (item?.errors?.length) {
                        error = true;
                    }
                    for (let i = 0; i < item.rows.length; i++) {
                        const row = item.rows[i];
                        if (row.errors?.length) {
                            error = true;
                            break;
                        }
                    }
                    return (
                        <Tab
                            style={{ color: error ? 'red' : undefined }}
                            label={`${item.name ? item.name : index}`}
                            value={index}
                            key={`${index}`}
                        />
                    );
                })}
            </Tabs>
            {data.map((item, index) => (
                <TabPanel selectedTab={selectedTab} index={index} key={`${index}`}>
                    {item?.errors?.length > 0 && <div>Errors In File: {item.errors}</div>}
                    {item?.warnings?.length > 0 && !(item?.errors?.length > 0) && (
                        <div style={{ maxHeight: '400px', overflowY: 'scroll' }}>
                            <div>Warnings</div>
                            <Alert severity="warning">{item.warnings}</Alert>
                        </div>
                    )}
                    {item.hasOwnProperty('info') && item.info.length && <div>{item.info}</div>}
                    <DataTable
                        columns={columns}
                        data={item.rows}
                        rowStyle={(row) => {
                            if (row.errors?.length) {
                                return { color: 'red', border: '3px solid red' };
                            }
                            if (row.warnings?.length) {
                                return { color: '#FFB300', border: '3px solid #FFB300' };
                            }
                            return {};
                        }}
                        csvExport={exportUploadResults}
                        data-testid="datatable"
                    />
                </TabPanel>
            ))}
        </Paper>
    );
}

function ToolbarButton({ isConfirming, item }) {
    const isConfirmButton = item.name === 'Confirm Upload';
    const isDisabled = item.disabled || (isConfirmButton && isConfirming);

    return (
        <Button
            color="secondary"
            style={{ marginLeft: '20px' }}
            key={item.name}
            variant="contained"
            disabled={isDisabled}
            onClick={async () => {
                await item.action();
            }}
            startIcon={isConfirmButton && isConfirming ? <CircularProgress size={20} color="inherit" /> : null}
        >
            {item.name}
        </Button>
    );
}

export { UploadCSVAndReviewComponent };
